import { calculateBoundingBox } from '@rio-cloud/rio-uikit/PositionUtils';
import type { BoundingBox, Position } from 'components/map/types';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import ExportPanel from './ExportPanel';
import { fetchHereRoute, type HereRoute } from './hereRouting';
import { simplifyToMax } from './interpolation';
import RouteBuilderMap from './RouteBuilderMap';
import RouteInfoPanel from './RouteInfoPanel';
import { isPlaced, type Waypoint } from './types';
import WaypointPanel from './WaypointPanel';
import './_styles.scss';

// Keeps the HERE request URL under ~5 KB
const MAX_ROUTING_WAYPOINTS = 100;
// Padding in degrees so a single pasted point is not shown at maximum zoom
const SINGLE_POINT_PADDING = 0.01;

const getFitBounds = (positions: Position[]): BoundingBox => {
    const box = calculateBoundingBox(positions);
    const pad = positions.length === 1 ? SINGLE_POINT_PADDING : 0;
    return { top: box.top + pad, bottom: box.bottom - pad, left: box.left - pad, right: box.right + pad };
};

const RouteBuilder = () => {
    const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
    // The route remembers which waypoints it was calculated for. Any edit creates a new waypoints array,
    // so an outdated route (or a late response for old waypoints) is never shown.
    const [routeResult, setRouteResult] = useState<{ waypoints: Waypoint[]; route: HereRoute }>();
    const [fitBounds, setFitBounds] = useState<BoundingBox>();

    const calculatedRoute = routeResult?.waypoints === waypoints ? routeResult.route : undefined;
    const positions = useMemo(() => waypoints.filter(isPlaced).map(w => w.position), [waypoints]);

    const addWaypoint = useCallback((position?: Position) => {
        setWaypoints(ws => [...ws, { id: uuidv4(), position }]);
    }, []);

    const updateWaypoint = useCallback((id: string, position: Position) => {
        setWaypoints(ws => ws.map(w => (w.id === id ? { ...w, position } : w)));
    }, []);

    const pasteWaypoints = (pasted: Position[]) => {
        setWaypoints(pasted.map(position => ({ id: uuidv4(), position })));
        setFitBounds(getFitBounds(pasted));
    };

    const calculateRoute = async () => {
        const requestedFor = waypoints;
        const kept = simplifyToMax(positions, MAX_ROUTING_WAYPOINTS);
        const route = await fetchHereRoute(positions.filter((_, i) => kept.has(i)));
        setRouteResult({ waypoints: requestedFor, route });
    };

    return (
        <div className='RouteBuilder'>
            <aside className='RouteBuilder__sidebar' onWheel={e => e.stopPropagation()}>
                <div className='RouteBuilder__sidebar-header'>
                    <span className='rioglyph rioglyph-route text-color-primary text-18' />
                    <span>Route Builder</span>
                </div>

                <div className='RouteBuilder__sidebar-body'>
                    <div className='RouteBuilder__section'>
                        <div className='RouteBuilder__section-label'>Waypoints</div>
                        <WaypointPanel
                            waypoints={waypoints}
                            setWaypoints={setWaypoints}
                            onAdd={addWaypoint}
                            onUpdate={updateWaypoint}
                            onPaste={pasteWaypoints}
                        />
                    </div>

                    <div className='RouteBuilder__section'>
                        <RouteInfoPanel positions={positions} route={calculatedRoute} onCalculate={calculateRoute} />
                    </div>

                    <div className='RouteBuilder__section'>
                        <div className='RouteBuilder__section-label'>Export</div>
                        <ExportPanel positions={positions} route={calculatedRoute} />
                    </div>
                </div>
            </aside>

            <div className='RouteBuilder__map'>
                <RouteBuilderMap
                    waypoints={waypoints}
                    route={calculatedRoute}
                    fitBounds={fitBounds}
                    onAdd={addWaypoint}
                    onUpdate={updateWaypoint}
                />
            </div>
        </div>
    );
};

export default RouteBuilder;
